import {
  MutationCache,
  QueryClient,
  queryOptions,
} from "@tanstack/react-query";
import {
  Account,
  ActivityQuery,
  APIError,
  ControlCode,
  ControlMonitorPoliciesParams,
  ControlMonitorSummaryParams,
  EndpointsParams,
  GroupParams,
  InboxesParams,
  LegacyEndpointsParams,
  PartnerAdvisory,
  UsersParams,
} from "@theprelude/sdk";
import { AuthConfig, service } from "../stores/auth-store";
import { defaultDateRange } from "../utils/date";

export const queryClient: QueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30_000),
      retry: 1,
      refetchOnReconnect: () => !queryClient.isMutating(),
    },
  },
  mutationCache: new MutationCache({
    onSettled: () => {
      if (queryClient.isMutating() === 1) {
        return queryClient.invalidateQueries();
      }
    },
  }),
});

type Features = keyof Account["features"];

function hasFeatures(account: Account, features: Features[]) {
  const accountFeatures = new Set(
    Object.entries(account.features)
      .filter(([, value]) => value)
      .map(([key]) => key),
  );

  return features.every((feature) => accountFeatures.has(feature));
}

function featuresQuery<T, K>(
  features: Features[],
  authConfig: AuthConfig,
  defaultValue: T,
  query: () => Promise<K>,
) {
  return async () => {
    const account = await queryClient.ensureQueryData(
      queries.iam.getAccount(authConfig),
    );
    return account && hasFeatures(account, features)
      ? await query()
      : defaultValue;
  };
}

type ExtractData<T extends Array<{ queryFn?: (...args: any[]) => any }>> = {
  [K in keyof T]: Awaited<
    ReturnType<Exclude<T[K]["queryFn"], null | undefined>>
  >;
};

export async function combineQueries<
  T extends { queryFn?: (...args: any[]) => any; queryKey: any }[],
>(...queries: [...T]): Promise<ExtractData<T>> {
  const results = await Promise.all(queries.map((query) => query.queryFn?.()));

  return results as ExtractData<T>;
}

export const defaultQuery: ActivityQuery = {
  start: defaultDateRange.start.toISOString(),
  finish: defaultDateRange.finish.toISOString(),
};

export type ControlMonitorFilters = {
  controlCode?: ControlCode;
  threatId?: string;
};

export const queries = {
  iam: {
    listAccounts: (authConfig: AuthConfig) =>
      queryOptions({
        queryKey: [authConfig, "iam", "listAccounts"] as const,
        queryFn: () => service(authConfig).iam.listAccounts(),
      }),
    getAccount: (authConfig: AuthConfig) =>
      queryOptions({
        queryKey: [authConfig, "iam", "getAccount"] as const,
        queryFn: () => service(authConfig).iam.getAccount(),
      }),
    getLogo: (authConfig: AuthConfig) =>
      queryOptions({
        queryKey: [authConfig, "iam", "getLogo"] as const,
        queryFn: async () => {
          try {
            return await service(authConfig).iam.getLogo();
          } catch (error) {
            if (error instanceof APIError && error.status === 404) {
              return null;
            }
            throw error;
          }
        },
      }),
  },
  build: {
    getCompile: (authConfig: AuthConfig, jobId: string) => ({
      queryKey: [authConfig, "build", jobId] as const,
      queryFn: () => service(authConfig).build.getCompile(jobId),
    }),
  },
  partner: {
    endpoints: (authConfig: AuthConfig, params: LegacyEndpointsParams) => ({
      queryKey: [authConfig, "partner", "endpoints", params] as const,
      queryFn: (offset = params.offset) =>
        service(authConfig).partner.endpoints({ ...params, offset }),
    }),
    group: (
      authConfig: AuthConfig,
      control: ControlCode,
      instanceId: string,
    ) => ({
      queryKey: [authConfig, "partner", "group", control, instanceId] as const,
      queryFn: () => service(authConfig).partner.group(control, instanceId),
    }),
    reports: (
      authConfig: AuthConfig,
      control: ControlCode,
      testId?: string,
    ) => ({
      queryKey: [authConfig, "partner", "reports", control, testId] as const,
      queryFn: featuresQuery(["detections"], authConfig, [], () =>
        service(authConfig).partner.partnerReports(control, testId),
      ),
    }),
    partnerAdvisories: (authConfig: AuthConfig, control: ControlCode) => ({
      queryKey: [authConfig, "partner", "partnerAdvisories", control] as const,
      queryFn: featuresQuery(["threat_intel"], authConfig, [], async () => {
        const { partner } = service(authConfig);
        const advisories: PartnerAdvisory[] = [];
        const pageSize = 200;
        let totalCount = 0;
        let offset = 0;
        do {
          const { advisories: page, pagination } =
            await partner.listPartnerAdvisories({
              control,
              offset: offset,
              limit: pageSize,
            });
          advisories.push(...page);
          totalCount = pagination.total;
          offset += pageSize;
        } while (advisories.length < totalCount);
        return advisories;
      }),
    }),
    ioaStats: (authConfig: AuthConfig, testId?: string) => ({
      queryKey: [authConfig, "partner", "ioaStats", testId] as const,
      queryFn: featuresQuery(["observed_detected"], authConfig, [], () =>
        service(authConfig).partner.ioaStats(testId),
      ),
    }),
    observedDetected: (authConfig: AuthConfig, testId?: string) => ({
      queryKey: [authConfig, "partner", "observedDetected", testId] as const,
      queryFn: featuresQuery(["observed_detected"], authConfig, undefined, () =>
        service(authConfig).partner.observedDetected(testId),
      ),
    }),
  },
  detect: {
    techniques: (authConfig: AuthConfig) => ({
      queryKey: [authConfig, "detect", "techniques", "list"] as const,
      queryFn: () => service(authConfig).detect.listTechniques(),
    }),
    tests: (authConfig: AuthConfig) => ({
      queryKey: [authConfig, "detect", "tests", "list"] as const,
      queryFn: () => service(authConfig).detect.listTests(),
    }),
    threats: (authConfig: AuthConfig) => ({
      queryKey: [authConfig, "detect", "threats", "list"] as const,
      queryFn: () => service(authConfig).detect.listThreats(),
    }),
    endpoints: (authConfig: AuthConfig, days: number) => ({
      queryKey: [authConfig, "detect", "probes", days] as const,
      queryFn: () => service(authConfig).detect.listEndpoints(days),
    }),
    detections: (authConfig: AuthConfig) => ({
      queryKey: [authConfig, "detect", "detections", "list"] as const,
      queryFn: featuresQuery(["detections"], authConfig, [], () =>
        service(authConfig).detect.listDetections(),
      ),
    }),
    threatHunts: (authConfig: AuthConfig) => ({
      queryKey: [authConfig, "detect", "threatHunts", "list"] as const,
      queryFn: featuresQuery(["threat_intel"], authConfig, [], () =>
        service(authConfig).detect.listThreatHunts(),
      ),
    }),
    getDetection: (authConfig: AuthConfig, detectionId: string) => ({
      queryKey: [authConfig, "detect", "getDetection", detectionId] as const,
      queryFn: featuresQuery(["detections"], authConfig, undefined, () =>
        service(authConfig).detect.getDetection(detectionId),
      ),
    }),
    activity: {
      logs: (authConfig: AuthConfig, query = defaultQuery) => ({
        queryKey: [authConfig, "detect", "activities", "logs", query] as const,
        queryFn: () =>
          service(authConfig).detect.describeActivity({
            view: "logs",
            ...query,
          }),
      }),
      tests: (authConfig: AuthConfig, query = defaultQuery) => ({
        queryKey: [authConfig, "detect", "activities", "tests", query] as const,
        queryFn: () =>
          service(authConfig).detect.describeActivity({
            view: "tests",
            ...query,
          }),
      }),
      techniques: (authConfig: AuthConfig, query = defaultQuery) => ({
        queryKey: [
          authConfig,
          "detect",
          "activities",
          "techniques",
          query,
        ] as const,
        queryFn: () =>
          service(authConfig).detect.describeActivity({
            view: "techniques",
            ...query,
          }),
      }),
      threats: (authConfig: AuthConfig, query = defaultQuery) => ({
        queryKey: [
          authConfig,
          "detect",
          "activities",
          "threats",
          query,
        ] as const,
        queryFn: () =>
          service(authConfig).detect.describeActivity({
            view: "threats",
            ...query,
          }),
      }),
      metrics: (authConfig: AuthConfig, query = defaultQuery) => ({
        queryKey: [
          authConfig,
          "detect",
          "activities",
          "metrics",
          query,
        ] as const,
        queryFn: () =>
          service(authConfig).detect.describeActivity({
            view: "metrics",
            ...query,
          }),
      }),
      findings: (authConfig: AuthConfig, query = defaultQuery) => ({
        queryKey: [
          authConfig,
          "detect",
          "activities",
          "findings",
          query,
        ] as const,
        queryFn: () =>
          service(authConfig).detect.describeActivity({
            view: "findings",
            ...query,
          }),
      }),
      protected: (authConfig: AuthConfig, query = defaultQuery) => ({
        queryKey: [
          authConfig,
          "detect",
          "activities",
          "protected",
          query,
        ] as const,
        queryFn: () =>
          service(authConfig).detect.describeActivity({
            view: "protected",
            ...query,
          }),
      }),
      endpoints: (authConfig: AuthConfig, query = defaultQuery) => ({
        queryKey: [
          authConfig,
          "detect",
          "activities",
          "endpoints",
          query,
        ] as const,
        queryFn: () =>
          service(authConfig).detect.describeActivity({
            view: "endpoints",
            ...query,
          }),
      }),
    },
    download: (authConfig: AuthConfig, testId: string, filename: string) => ({
      queryKey: [authConfig, "detect", "download", testId, filename] as const,
      queryFn: () => service(authConfig).detect.download(testId, filename),
    }),

    getReadMe: (authConfig: AuthConfig, testId: string) => ({
      queryKey: [authConfig, "detect", "getReadMe", testId] as const,
      queryFn: async () => {
        try {
          const readme = await service(authConfig).detect.download(
            testId,
            "README.md",
          );

          return readme;
        } catch {
          return "";
        }
      },
    }),
    getSourceCode: (authConfig: AuthConfig, testId: string) => ({
      queryKey: [authConfig, "detect", "getSourceCode", testId] as const,
      queryFn: async () => {
        try {
          const sourceCode = await service(authConfig).detect.download(
            testId,
            `${testId}.go`,
          );

          return sourceCode;
        } catch {
          return "";
        }
      },
    }),
    getDetectionCode: (authConfig: AuthConfig, testId: string) => ({
      queryKey: [authConfig, "detect", "getDetectionCode", testId] as const,
      queryFn: async () => {
        try {
          const sourceCode = await service(authConfig).detect.download(
            testId,
            `${testId}.yml`,
          );

          return sourceCode;
        } catch {
          return "";
        }
      },
    }),
    getTest: (authConfig: AuthConfig, testId: string) => ({
      queryKey: [authConfig, "detect", "getTest", testId] as const,
      queryFn: () => service(authConfig).detect.getTest(testId),
    }),
    getThreat: (authConfig: AuthConfig, threatId: string) => ({
      queryKey: [authConfig, "detect", "getThreat", threatId] as const,
      queryFn: () => service(authConfig).detect.getThreat(threatId),
    }),
    getThreatHunt: (authConfig: AuthConfig, threatHuntId: string) => ({
      queryKey: [authConfig, "detect", "getThreatHunt", threatHuntId] as const,
      queryFn: featuresQuery(["threat_intel"], authConfig, undefined, () =>
        service(authConfig).detect.getThreatHunt(threatHuntId),
      ),
    }),
    getThreatHuntActivity: (
      authConfig: AuthConfig,
      id: string,
      type: "threat_hunt" | "test" | "threat",
    ) => ({
      queryKey: [authConfig, "detect", "getThreatHuntActivity", id] as const,
      queryFn: featuresQuery(["threat_intel"], authConfig, [], () =>
        service(authConfig).detect.threatHuntActivity(id, type),
      ),
    }),
    getAlerts: (authConfig: AuthConfig) => ({
      queryKey: [authConfig, "detect", "getAlerts"] as const,
      queryFn: async () => service(authConfig).detect.getAlerts(),
    }),
  },
  generate: {
    getThreatIntel: (authConfig: AuthConfig, id: string) => ({
      queryKey: [authConfig, "generate", "getThreatIntel", id] as const,
      queryFn: featuresQuery(["threat_intel"], authConfig, undefined, () =>
        service(authConfig).generate.getThreatIntel(id),
      ),
    }),
  },
  scm: {
    endpoints: (authConfig: AuthConfig, params: EndpointsParams = {}) => ({
      queryKey: [authConfig, "scm", "endpoints", params] as const,
      queryFn: () => service(authConfig).scm.endpoints(params),
    }),
    users: (authConfig: AuthConfig, params: UsersParams = {}) => ({
      queryKey: [authConfig, "scm", "users", params] as const,
      queryFn: () => service(authConfig).scm.users(params),
    }),
    inboxes: (authConfig: AuthConfig, params: InboxesParams = {}) => ({
      queryKey: [authConfig, "scm", "inboxes", params] as const,
      queryFn: () => service(authConfig).scm.inboxes(params),
    }),
    views: (authConfig: AuthConfig) => ({
      queryKey: [authConfig, "scm", "views"] as const,
      queryFn: () => service(authConfig).scm.listViews(),
    }),
    groups: (authConfig: AuthConfig, params: GroupParams = {}) => ({
      queryKey: [authConfig, "scm", "groups", params] as const,
      queryFn: () => service(authConfig).scm.groups(params),
    }),
    summary: (
      authConfig: AuthConfig,
      params: ControlMonitorSummaryParams = {},
    ) => ({
      queryKey: [authConfig, "scm", "summary", params] as const,
      queryFn: () => service(authConfig).scm.getControlMonitorSummary(params),
    }),
    techniqueSummaries: (authConfig: AuthConfig, techniques: string[]) => ({
      queryKey: [authConfig, "scm", "techniqueSummaries", techniques] as const,
      queryFn: () => service(authConfig).scm.getTechniqueSummaries(techniques),
    }),
    policies: (
      authConfig: AuthConfig,
      controlCode: ControlCode,
      instanceId: string,
      params?: ControlMonitorPoliciesParams,
    ) => ({
      queryKey: [
        authConfig,
        "scm",
        "policies",
        controlCode,
        instanceId,
        params,
      ] as const,
      queryFn: () =>
        service(authConfig).scm.listPolicies(controlCode, instanceId, params),
    }),
    objectExceptions: (authConfig: AuthConfig) => ({
      queryKey: [authConfig, "scm", "objectExceptions"] as const,
      queryFn: () => service(authConfig).scm.objectExceptions(),
    }),
    policyExceptions: (authConfig: AuthConfig) => ({
      queryKey: [authConfig, "scm", "policyExceptions"] as const,
      queryFn: () => service(authConfig).scm.policyExceptions(),
    }),
    notifications: (authConfig: AuthConfig) => ({
      queryKey: [authConfig, "scm", "notifications"] as const,
      queryFn: () => service(authConfig).scm.listNotifications(),
    }),
    threats: (authConfig: AuthConfig) => ({
      queryKey: [authConfig, "scm", "threats"] as const,
      queryFn: () => service(authConfig).scm.listThreats(),
    }),
    getThreat: (authConfig: AuthConfig, id: string) => ({
      queryKey: [authConfig, "scm", "getThreat", id] as const,
      queryFn: () => service(authConfig).scm.getThreat(id),
    }),
  },
  export: {
    downloadEndpoints: (authConfig: AuthConfig, params: EndpointsParams) => ({
      queryKey: [authConfig, "export", "downloadEndpoints", params] as const,
      queryFn: featuresQuery(["policy_evaluator"], authConfig, undefined, () =>
        service(authConfig).export.downloadEndpointsCsv(params),
      ),
    }),
    downloadUsers: (authConfig: AuthConfig, params: UsersParams) => ({
      queryKey: [authConfig, "export", "downloadUsers", params] as const,
      queryFn: featuresQuery(["policy_evaluator"], authConfig, undefined, () =>
        service(authConfig).export.downloadUsersCsv(params),
      ),
    }),
    downloadInboxes: (authConfig: AuthConfig, params: InboxesParams) => ({
      queryKey: [authConfig, "export", "downloadUser", params] as const,
      queryFn: featuresQuery(["policy_evaluator"], authConfig, undefined, () =>
        service(authConfig).export.downloadInboxesCsv(params),
      ),
    }),
  },
  jobs: {
    getJobStatuses: (authConfig: AuthConfig) => ({
      queryKey: [authConfig, "jobs", "getJobStatuses"] as const,
      queryFn: () => service(authConfig).jobs.getJobStatuses(),
    }),
    getJobStatus: (authConfig: AuthConfig, jobId: string) => ({
      queryKey: [authConfig, "job", "getJobStatus", jobId],
      queryFn: () => service(authConfig).jobs.getJobStatus(jobId),
    }),
  },
};
